/**
 * tileAutotile — pure 4-bit neighbor-mask tile-key picker (POLISH_02 #5).
 *
 * Extends the old 2-way autotile (top-open vs buried — `tile_solid_top` /
 * `tile_solid_fill`) to also read LEFT/RIGHT solidity so a SOLID run gets edge
 * caps + corners instead of repeating one texture across ends and buried faces.
 * SEMISOLID is untouched (still a flat `tile_semisolid` — out of scope per the
 * finding: "semisolid tiles keep current behavior unless trivially improved").
 *
 * Pure function — no Pixi/DOM/grid coupling — so `World.buildTiles` can call it
 * with three booleans read from `TileGrid.isSolid`. Kept in its own module (not
 * inlined in World.ts) so it is unit-testable without constructing a World.
 *
 * Determinism: three booleans in, one string out — no math, no branching on
 * anything but the inputs. Lint-clean under src/engine/ (no transcendentals).
 */

/** Solid-neighbor mask for the cell being picked. All three are read from the
 *  SAME grid the sim collides against (TileGrid.isSolid), so mask + collision
 *  never disagree. */
export interface SolidNeighborMask {
  /** Is the tile directly ABOVE this one also SOLID? (false = this is a top surface). */
  top: boolean;
  /** Is the tile directly LEFT of this one also SOLID? */
  left: boolean;
  /** Is the tile directly RIGHT of this one also SOLID? */
  right: boolean;
}

/**
 * Pick the logical sprite key for one SOLID cell from its 3-neighbor mask.
 *
 * Rows:
 *  - top-open + no left/right neighbor at all → `tile_solid_block` (the standalone
 *    full-block key — REQUIRED + vendored since Brief 006 but never consumed by
 *    buildTiles until now). This covers BOTH a fully isolated 1x1 floating block
 *    and the top cell of an isolated 1-wide surface pillar — the 3-bit mask can't
 *    tell those apart (both read top:false, left:false, right:false) and Kenney's
 *    set has no separate "single column top" asset, so one key correctly covers
 *    both.
 *  - top-open (surface row), otherwise: left+right solid → `tile_solid_top`
 *    (unchanged 2-way case); missing exactly one side → the matching top corner.
 *  - buried (top solid): left+right solid → `tile_solid_fill` (unchanged 2-way
 *    case); missing exactly one side → the matching buried edge cap; missing BOTH
 *    sides → still `tile_solid_fill` (a 1-wide buried column has no dedicated
 *    Kenney asset — falls back to the existing fill rather than inventing an
 *    unshipped file).
 */
export function pickTileKey(mask: SolidNeighborMask): string {
  const { top, left, right } = mask;
  if (!top) {
    // Surface row (nothing solid above this cell).
    if (left && right) return 'tile_solid_top';
    if (left && !right) return 'tile_solid_top_right'; // right end of a surface run
    if (!left && right) return 'tile_solid_top_left'; // left end of a surface run
    return 'tile_solid_block'; // top-open, isolated left+right — standalone block
  }
  // Buried row (solid above this cell).
  if (left && right) return 'tile_solid_fill';
  if (left && !right) return 'tile_solid_right'; // right edge of a buried run
  if (!left && right) return 'tile_solid_left'; // left edge of a buried run
  return 'tile_solid_fill'; // buried + isolated column — no dedicated asset, keep fill
}
